import { DOCUMENT } from '@angular/common'
import { Inject, Injectable } from '@angular/core'
import {
    DOJO_CLIENT_SCRIPT_URL,
    DojoClient,
    DojoWindow,
} from './dojo-client'

@Injectable({ providedIn: 'root' })
export class DojoClientLoader {

    private loading: Promise<DojoClient> | null = null

    constructor(@Inject(DOCUMENT) private readonly document: Document) { }

    load(): Promise<DojoClient> {
        const dojoWindow = this.document.defaultView as DojoWindow | null
        if (!dojoWindow) {
            return Promise.reject(new Error('Dojo payments require a browser window'))
        }
        if (dojoWindow.Dojo) {
            return Promise.resolve(dojoWindow.Dojo)
        }
        if (this.loading) {
            return this.loading
        }

        this.loading = new Promise<DojoClient>((resolve, reject) => {
            // A connected script with no global may already have fired load/error.
            // Replace it so this loader always receives a terminal event.
            this.existingScript()?.remove()
            const script = this.createScript()
            const onLoad = () => {
                cleanup()
                if (!dojoWindow.Dojo) {
                    script.remove()
                    this.loading = null
                    reject(new Error('Dojo payments client loaded without exposing Dojo'))
                    return
                }
                resolve(dojoWindow.Dojo)
            }
            const onError = () => {
                cleanup()
                script.remove()
                this.loading = null
                reject(new Error('Unable to load Dojo payments. Please refresh the page and try again.'))
            }
            const cleanup = () => {
                script.removeEventListener('load', onLoad)
                script.removeEventListener('error', onError)
            }
            script.addEventListener('load', onLoad)
            script.addEventListener('error', onError)
            this.document.head.appendChild(script)
        })
        return this.loading
    }

    private existingScript(): HTMLScriptElement | null {
        return this.document.querySelector(`script[src="${DOJO_CLIENT_SCRIPT_URL}"]`)
    }

    private createScript(): HTMLScriptElement {
        const script = this.document.createElement('script')
        script.src = DOJO_CLIENT_SCRIPT_URL
        script.async = true
        return script
    }
}
